// Frontend "closest geodata" search for the venue_geodata_miss HITL resolver.
// Mirrors tina's backend recipe (src/pipeline/shared_stages/venue_resolution/
// geodata.py): a Typesense geo filter `geolocation:(lat, lng, R km)` sorted by
// `geolocation(lat, lng):asc`, with a haversine fallback for the per-hit
// distance when Typesense's geo_distance_meters isn't present.

export interface Coords {
  lat: number;
  lng: number;
}

export interface NearestGeodata {
  id: string;
  name: string;
  countryCode?: string;
  region?: string;
  type?: string;
  distanceKm: number;
}

const EARTH_RADIUS_KM = 6371;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance between two coordinates, in kilometres. */
export function haversineKm(a: Coords, b: Coords): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
}

export interface NearestGeodataQueryOptions {
  radiusKm?: number;
  limit?: number;
}

/** Query params for the geodata `explore` endpoint that return nearby cities. */
export function buildNearestGeodataQuery(
  { lat, lng }: Coords,
  { radiusKm = 200, limit = 5 }: NearestGeodataQueryOptions = {},
) {
  return {
    q: "*",
    // The explore endpoint requires an int `page` (its deserializer rejects a
    // missing one); we only ever need the first page of nearest hits.
    page: 1,
    queryBy: "name",
    filters: `type:[CITY,ISLAND] && geolocation:(${lat}, ${lng}, ${radiusKm} km)`,
    sortBy: `geolocation(${lat}, ${lng}):asc`,
    includeFields: "id,name,countryCode,region,type,geolocation,hide",
    limit,
  };
}

function toStr(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (typeof value === "number") return String(value);
  return undefined;
}

function hitDistanceKm(
  hit: Record<string, unknown>,
  doc: Record<string, unknown>,
  origin: Coords,
): number {
  const geoDistance = (hit.geo_distance_meters ?? {}) as Record<
    string,
    unknown
  >;
  const meters = geoDistance.geolocation;
  if (typeof meters === "number") return meters / 1000;

  const geo = (doc.geolocation ?? {}) as Record<string, unknown>;
  const lat = geo.latitude;
  const lng = geo.longitude;
  if (typeof lat === "number" && typeof lng === "number") {
    return haversineKm(origin, { lat, lng });
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * Turn raw explore hits into the closest `limit` visible geodata records,
 * each carrying a display distance. Hidden records are dropped; results are
 * distance-sorted so a haversine fallback still orders correctly.
 */
export function parseNearestGeodata(
  hits: unknown,
  origin: Coords,
  limit = 2,
): NearestGeodata[] {
  const list = Array.isArray(hits) ? hits : [];
  return list
    .flatMap((raw) => {
      const hit = (raw ?? {}) as Record<string, unknown>;
      const doc = (hit.document ?? {}) as Record<string, unknown>;
      const id = toStr(doc.id);
      const name = toStr(doc.name);
      if (!id || !name) return [];
      if (doc.hide === true) return [];
      return [
        {
          id,
          name,
          countryCode: toStr(doc.countryCode),
          region: toStr(doc.region),
          type: toStr(doc.type),
          distanceKm: hitDistanceKm(hit, doc, origin),
        },
      ];
    })
    .sort((a, b) => a.distanceKm - b.distanceKm)
    .slice(0, limit);
}
